import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const Login = () => {
  const navigate = useNavigate();
  const { login, loginWithGoogle, isAuthenticated } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [status, setStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const finalValue = name === 'email' ? value.toLowerCase() : value;
    setFormData({ ...formData, [name]: finalValue });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.password) {
      setStatus({ type: 'warning', text: 'Email and password are required.' });
      return;
    }

    setIsSubmitting(true);
    try {
      const userData = await login(formData.email, formData.password);
      sessionStorage.setItem('rzHasLoggedIn', '1'); // mark: known user this session
      const userName = userData?.firstName || 'there';
      setStatus({ type: 'success', text: `Welcome back, ${userName}!` });
      setTimeout(() => navigate('/dashboard'), 800);
    } catch (err) {
      if (err.response?.status === 422) {
        const detail = err.response.data.detail;
        const errorMsg = Array.isArray(detail) ? detail[0].msg : detail;
        setStatus({ type: 'error', text: errorMsg });
      } else {
        setStatus({ type: 'error', text: err.response?.data?.detail || 'Invalid credentials' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleLogin = () => {
    setStatus({ type: 'info', text: 'Redirecting to Google...' });
    loginWithGoogle();
  };

  const statusColors = {
    success: { color: 'var(--color-success)', bg: 'var(--color-success-bg)', border: 'var(--color-success-border)' },
    error:   { color: 'var(--color-error)',   bg: 'var(--color-error-bg)',   border: 'var(--color-error-border)'   },
    warning: { color: 'var(--color-warning)', bg: 'var(--color-warning-bg)', border: 'var(--color-warning-border)' },
    info:    { color: 'var(--color-text-secondary)', bg: 'var(--color-surface-alt)', border: 'var(--color-border)' },
  };

  return (
    <div style={styles.page}>
      {/* Decorative background blobs */}
      <div className="auth-blob-decoration" style={styles.blobTopRight} aria-hidden="true" />
      <div className="auth-blob-decoration" style={styles.blobBottomLeft} aria-hidden="true" />

      <main style={styles.card} className="glass-panel auth-card-container">
        {/* Brand */}
        <header style={styles.brandArea}>
          <span style={styles.logoMark} aria-hidden="true">⬡</span>
          <h1 style={styles.brandName}>RezFlow</h1>
        </header>

        <p style={styles.tagline}>Sign in to your workspace</p>

        {/* Google SSO */}
        <button
          type="button"
          onClick={handleGoogleLogin}
          style={styles.googleBtn}
          disabled={isSubmitting}
          className="hover-lift"
          aria-label="Continue with Google"
        >
          <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
          </svg>
          Continue with Google
        </button>

        {/* Divider */}
        <div style={styles.divider} role="separator">
          <span style={styles.dividerLine} />
          <span style={styles.dividerText}>or</span>
          <span style={styles.dividerLine} />
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={styles.form} noValidate>
          <div style={styles.fieldWrap}>
            <label htmlFor="login-email" style={styles.label}>Email</label>
            <input
              id="login-email"
              name="email"
              type="email"
              placeholder="you@example.com"
              value={formData.email}
              onChange={handleChange}
              onFocus={() => setFocusedField('email')}
              onBlur={() => setFocusedField(null)}
              style={{
                ...styles.input,
                ...(focusedField === 'email' ? styles.inputFocused : {}),
              }}
              disabled={isSubmitting}
              autoComplete="email"
              required
            />
          </div>

          <div style={styles.fieldWrap}>
            <div style={styles.labelRow}>
              <label htmlFor="login-password" style={styles.label}>Password</label>
              <Link to="/forgot-password" style={styles.forgotLink}>Forgot?</Link>
            </div>
            <input
              id="login-password"
              name="password"
              type="password"
              placeholder="••••••••"
              value={formData.password}
              onChange={handleChange}
              onFocus={() => setFocusedField('password')}
              onBlur={() => setFocusedField(null)}
              style={{
                ...styles.input,
                ...(focusedField === 'password' ? styles.inputFocused : {}),
              }}
              disabled={isSubmitting}
              autoComplete="current-password"
              required
            />
          </div>

          <button
            type="submit"
            style={{ ...styles.submitBtn, opacity: isSubmitting ? 0.65 : 1 }}
            disabled={isSubmitting}
            className="hover-lift"
          >
            {isSubmitting ? (
              <span style={styles.btnInner}>
                <span style={styles.btnSpinner} />
                Signing in…
              </span>
            ) : 'Sign In'}
          </button>
        </form>

        {/* Status message */}
        {status && (
          <div
            role="status"
            aria-live="polite"
            style={{
              ...styles.statusMsg,
              color: statusColors[status.type]?.color,
              backgroundColor: statusColors[status.type]?.bg,
              borderColor: statusColors[status.type]?.border,
            }}
          >
            {status.text}
          </div>
        )}

        {/* Footer link */}
        <p style={styles.footerLink}>
          Don't have an account?{' '}
          <Link to="/signup" style={styles.link}>Create one</Link>
        </p>
      </main>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100dvh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px 20px',
    background: 'linear-gradient(145deg, #f0ede7 0%, #e8eee5 55%, #e7ecf0 100%)',
    position: 'relative',
    overflow: 'hidden',
  },
  blobTopRight: {
    position: 'absolute',
    top: '-120px',
    right: '-140px',
    width: '420px',
    height: '420px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(245, 252, 248, 0.08) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  blobBottomLeft: {
    position: 'absolute',
    bottom: '-100px',
    left: '-120px',
    width: '360px',
    height: '360px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(239, 245, 238, 0.07) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  card: {
    position: 'relative',
    zIndex: 1,
    width: '100%',
    maxWidth: '440px',
    borderRadius: 'var(--radius-2xl)',
    padding: '40px 40px 36px',
    animation: 'rz-fadeIn 0.4s ease-out both',
  },
  brandArea: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    marginBottom: '6px',
  },
  logoMark: {
    fontSize: '24px',
    color: 'var(--color-charcoal-800)',
    lineHeight: 1,
  },
  brandName: {
    fontFamily: 'var(--font-heading)',
    fontSize: '28px',
    fontWeight: 700,
    letterSpacing: '-0.5px',
    color: 'var(--color-charcoal-900)',
    margin: 0,
  },
  tagline: {
    textAlign: 'center',
    color: 'var(--color-text-secondary)',
    fontSize: 'var(--text-sm)',
    marginBottom: '28px',
    fontWeight: 'var(--font-weight-medium)',
  },
  googleBtn: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    padding: '13px 20px',
    backgroundColor: 'var(--color-surface)',
    border: '1px solid var(--color-border-strong)',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--text-sm)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-primary)',
    transition: 'all var(--transition-base)',
    boxShadow: 'var(--shadow-xs)',
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    margin: '24px 0',
  },
  dividerLine: {
    flex: 1,
    height: '1px',
    backgroundColor: 'var(--color-border)',
  },
  dividerText: {
    fontSize: 'var(--text-xs)',
    color: 'var(--color-text-muted)',
    fontWeight: 'var(--font-weight-medium)',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '18px',
  },
  fieldWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: '7px',
  },
  label: {
    fontSize: 'var(--text-sm)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-primary)',
  },
  labelRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  forgotLink: {
    fontSize: 'var(--text-xs)',
    color: 'var(--color-text-secondary)',
    fontWeight: 'var(--font-weight-medium)',
    transition: 'color var(--transition-fast)',
  },
  input: {
    padding: '13px 14px',
    border: '1.5px solid var(--color-border-strong)',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--text-sm)',
    color: 'var(--color-text-primary)',
    backgroundColor: 'var(--color-surface)',
    outline: 'none',
    transition: 'border-color var(--transition-base), box-shadow var(--transition-base)',
    width: '100%',
  },
  inputFocused: {
    borderColor: 'var(--color-charcoal-700)',
    boxShadow: 'var(--glow-accent)',
  },
  submitBtn: {
    width: '100%',
    padding: '14px 20px',
    background: '#ffffff',
    color: '#000000',
    border: '1px solid var(--color-border-strong)',
    borderRadius: 'var(--radius-md)',
    fontFamily: 'var(--font-heading)',
    fontWeight: 'var(--font-weight-bold)',
    fontSize: 'var(--text-base)',
    letterSpacing: '0.01em',
    marginTop: '4px',
    transition: 'opacity var(--transition-base), transform var(--transition-base), box-shadow var(--transition-base)',
    boxShadow: '0 4px 14px rgba(13,15,13,0.18)',
  },
  btnInner: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '10px',
  },
  btnSpinner: {
    display: 'inline-block',
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    border: '2px solid rgba(0,0,0,0.1)',
    borderTopColor: '#000',
    animation: 'rz-spin 0.8s linear infinite',
  },
  statusMsg: {
    marginTop: '18px',
    padding: '12px 14px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid',
    fontSize: 'var(--text-sm)',
    fontWeight: 'var(--font-weight-medium)',
    textAlign: 'center',
    animation: 'rz-fadeInFlat 0.2s ease-out',
  },
  footerLink: {
    textAlign: 'center',
    marginTop: '24px',
    fontSize: 'var(--text-sm)',
    color: 'var(--color-text-secondary)',
  },
  link: {
    color: 'var(--color-charcoal-800)',
    fontWeight: 'var(--font-weight-semibold)',
    borderBottom: '1px solid var(--color-border-strong)',
    transition: 'border-color var(--transition-fast)',
  },
};

export default Login;